import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Heart, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import './RegulationMode.css';

// Book quotes - exactly as specified
const bookQuotes = [
  "You do not have to solve it all right now.",
  "Breathing is not a pause from parenting. It is part of it.",
  "The overwhelm is real. So is your capacity to move through it.",
  "You are still showing up. That counts.",
  "Rest is not a reward. It is a requirement."
];

// Daily affirmations for dashboard
const dailyAffirmations = [
  { day: 0, quote: "You are not behind. You are carrying more than anyone can see.", chapter: "Chapter 1" },
  { day: 1, quote: "The mental load is not the tasks themselves. It is the knowing. The remembering. The planning.", chapter: "Chapter 2" },
  { day: 2, quote: "It is not a character flaw. It is what happens when one person becomes the default keeper of a family's entire operating system.", chapter: "Chapter 3" },
  { day: 3, quote: "Breathe first. The answer can wait. Your peace cannot.", chapter: "Chapter 4" },
  { day: 4, quote: "You don't have to be perfect. You just have to be present.", chapter: "Chapter 5" },
  { day: 5, quote: "Rest is not a reward for finishing. Rest is how you keep going.", chapter: "Chapter 6" },
  { day: 6, quote: "Letting go of perfection is not giving up. It is making room for connection.", chapter: "Chapter 7" },
];

// Get today's affirmation
export const getTodayAffirmation = () => {
  const dayOfWeek = new Date().getDay();
  return dailyAffirmations[dayOfWeek];
};

// Regulation Mode Button Component (for sidebar)
export const RegulationModeButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-nurture-terracotta/10 to-nurture-sage/10 border-2 border-dashed border-nurture-terracotta/30 text-nurture-terracotta hover:border-nurture-terracotta hover:bg-nurture-terracotta/10 transition-all duration-300 group"
      data-testid="overwhelmed-btn"
    >
      <div className="w-8 h-8 rounded-full bg-nurture-terracotta/20 flex items-center justify-center group-hover:bg-nurture-terracotta/30 transition-colors">
        <Heart className="w-4 h-4 text-nurture-terracotta" />
      </div>
      <span className="font-medium text-sm">I'm overwhelmed</span>
    </button>
  );
};

// Breathing phases: 4s inhale, 2s hold, 4s exhale, 1s rest = 11s total
const PHASE_DURATIONS = {
  inhale: 4000,
  hold: 2000,
  exhale: 4000,
  rest: 1000
};

const PHASE_INSTRUCTIONS = {
  inhale: "Breathe in...",
  hold: "Hold...",
  exhale: "Let it go...",
  rest: "Rest..."
};

// Main Regulation Mode Modal Component
export const RegulationModeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('inhale');
  const [cycleCount, setCycleCount] = useState(1);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const animationRef = useRef(null);
  const phaseTimeoutRef = useRef(null);

  // Start breathing cycle immediately when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset state
      setPhase('inhale');
      setCycleCount(1);
      setQuoteIndex(0);
      
      // Start the breathing cycle
      startBreathingCycle();
    }
    
    return () => {
      if (phaseTimeoutRef.current) {
        clearTimeout(phaseTimeoutRef.current);
      }
    };
  }, [isOpen]);

  const startBreathingCycle = () => {
    runPhase('inhale');
  };

  const runPhase = (currentPhase) => {
    setPhase(currentPhase);
    
    phaseTimeoutRef.current = setTimeout(() => {
      const nextPhase = getNextPhase(currentPhase);
      
      // If completing a full cycle (going back to inhale)
      if (nextPhase === 'inhale') {
        setCycleCount(prev => prev + 1);
        setQuoteIndex(prev => (prev + 1) % bookQuotes.length);
      }
      
      runPhase(nextPhase);
    }, PHASE_DURATIONS[currentPhase]);
  };

  const getNextPhase = (current) => {
    const phases = ['inhale', 'hold', 'exhale', 'rest'];
    const currentIndex = phases.indexOf(current);
    return phases[(currentIndex + 1) % phases.length];
  };

  const handleRespondLater = () => {
    if (phaseTimeoutRef.current) clearTimeout(phaseTimeoutRef.current);
    onClose();
  };

  const handleHandoffNow = () => {
    if (phaseTimeoutRef.current) clearTimeout(phaseTimeoutRef.current);
    onClose();
    navigate('/handoff');
  };

  const handleImReady = () => {
    if (phaseTimeoutRef.current) clearTimeout(phaseTimeoutRef.current);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden rounded-3xl border-none bg-gradient-to-b from-nurture-cream to-white">
        {/* Header */}
        <div className="relative p-6 pb-0">
          <button
            onClick={handleImReady}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-nurture-cream-dark transition-colors z-10"
            data-testid="close-regulation-modal"
          >
            <X className="w-5 h-5 text-nurture-muted" />
          </button>
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold text-nurture-navy">
              Take a breath
            </h2>
            <p className="text-nurture-muted mt-1">You've got this. Let's slow down together.</p>
          </div>
        </div>

        {/* Breathing Circle - Starts animating immediately */}
        <div className="flex flex-col items-center justify-center py-8 px-6">
          <div className="text-center">
            {/* CSS-animated breathing circle */}
            <div 
              ref={animationRef}
              className={`breathing-circle breathing-circle--${phase}`}
              data-testid="breathing-circle"
            >
              <span className="breathing-circle__text">
                {PHASE_INSTRUCTIONS[phase]}
              </span>
            </div>

            {/* Cycle counter */}
            <div className="flex items-center justify-center gap-2 text-nurture-muted mt-6">
              <Clock className="w-4 h-4" />
              <span data-testid="cycle-counter">Cycle {cycleCount}</span>
            </div>
          </div>
        </div>

        {/* Quote Section */}
        <div className="bg-nurture-cream-dark/50 p-6">
          <div className="text-center">
            <p 
              className="text-nurture-navy italic text-lg leading-relaxed"
              data-testid="book-quote"
            >
              "{bookQuotes[quoteIndex]}"
            </p>
            <p className="text-sm text-nurture-muted mt-2">— Breathe, Then Answer</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 pt-4 grid grid-cols-3 gap-3">
          <Button
            variant="outline"
            className="rounded-xl py-6 flex flex-col items-center gap-1 h-auto hover:bg-nurture-maize/20 hover:border-nurture-maize"
            onClick={handleRespondLater}
            data-testid="respond-later-btn"
          >
            <Clock className="w-5 h-5 text-nurture-maize" />
            <span className="text-xs font-medium">Respond later</span>
          </Button>
          <Button
            variant="outline"
            className="rounded-xl py-6 flex flex-col items-center gap-1 h-auto hover:bg-nurture-sage/20 hover:border-nurture-sage"
            onClick={handleHandoffNow}
            data-testid="handoff-now-btn"
          >
            <ArrowRight className="w-5 h-5 text-nurture-sage" />
            <span className="text-xs font-medium">Handoff now</span>
          </Button>
          <Button
            className="rounded-xl py-6 flex flex-col items-center gap-1 h-auto bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white"
            onClick={handleImReady}
            data-testid="im-ready-btn"
          >
            <Heart className="w-5 h-5" />
            <span className="text-xs font-medium">I'm ready</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RegulationModeModal;
